"use client";

import { ArrowUpRight } from "lucide-react";
import { ReactNode, useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { track } from "@/lib/tracking";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from "@/components/ui/carousel";
import Image from "next/image";

interface VideoTab {
  key: string;
  label: string | ReactNode;
  videoUrl?: string;
  imageUrl?: string;
}

interface ImageTab {
  key: string;
  label: string | ReactNode;
  imageUrls: string[];
}

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  details: string[];
  link?: string;
  linkText?: string;
  mediaPlaceholder?: string;
  singleVideoUrl?: string;
  singleImageUrl?: string;
  multipleImageUrls?: string[];
  videoTabs?: VideoTab[];
  imageTabs?: ImageTab[];
}

const FeatureCard = ({
  icon,
  title,
  description,
  details,
  link,
  linkText,
  mediaPlaceholder,
  singleVideoUrl,
  singleImageUrl,
  multipleImageUrls,
  videoTabs,
  imageTabs,
}: FeatureCardProps) => {
  const [api, setApi] = useState<CarouselApi>();

  // Auto-scroll carousel every 3 seconds
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 3000);

    return () => clearInterval(interval);
  }, [api]);

  // Convert YouTube URL to embed format
  const getEmbedUrl = (url: string) => {
    const videoId = url.split("/").pop()?.split("?")[0];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
      <div className="w-full md:w-2/5 space-y-4 md:pl-[2cm]">
        <div className="flex flex-col items-start gap-3">
          <div className="p-2 bg-secondary rounded-lg">{icon}</div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground [word-spacing:0.1em] text-balance">
            {title}
          </h3>
        </div>

        <div className="space-y-2">
          {details.map((detail, index) => (
            <p
              key={index}
              className="text-xs md:text-sm text-foreground/80 leading-relaxed mr-[5px]"
            >
              {detail}
            </p>
          ))}
        </div>

        {link && linkText && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track("cta_click", {
                cta_label: linkText,
                cta_location: "features",
                target_url: link,
              });
              track("external_link_to_teacher", {
                link_type: "signup",
                source_section: "features",
              });
            }}
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium group"
          >
            {linkText}
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        )}
      </div>

      <div className="w-full md:w-3/5 md:pr-[2cm]">
        {singleImageUrl ? (
          <div className="bg-background p-4 md:p-6 rounded-xl shadow-lg border border-border">
            <div className="aspect-video bg-muted rounded-lg overflow-hidden border border-border relative">
              <Image
                src={singleImageUrl}
                alt={title}
                fill
                className="object-cover"
                loading="lazy"
                unoptimized
              />
            </div>
          </div>
        ) : multipleImageUrls ? (
          <div className="bg-background p-4 md:p-6 rounded-xl shadow-lg border border-border">
            <div className="bg-muted rounded-lg overflow-hidden border border-border p-2 md:p-4">
              <div className="relative">
                <Carousel
                  setApi={setApi}
                  opts={{ loop: true }}
                  className="w-full"
                >
                  <CarouselContent>
                    {multipleImageUrls.map((url, idx) => (
                      <CarouselItem key={idx} className="basis-full">
                        <div className="aspect-video flex items-center justify-center relative">
                          <Image
                            src={url}
                            alt={`${title} ${idx + 1}`}
                            fill
                            className="object-contain rounded-lg"
                            loading="lazy"
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
                <button
                  onClick={() => api?.scrollPrev()}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-background/90 border border-border hover:bg-accent transition-colors shadow-lg z-10"
                  aria-label="Previous image"
                >
                  <span className="text-foreground text-xl">&lt;</span>
                </button>
                <button
                  onClick={() => api?.scrollNext()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-background/90 border border-border hover:bg-accent transition-colors shadow-lg z-10"
                  aria-label="Next image"
                >
                  <span className="text-foreground text-xl">&gt;</span>
                </button>
              </div>
            </div>
          </div>
        ) : singleVideoUrl ? (
          <div className="bg-background p-4 md:p-6 rounded-xl shadow-lg border border-border">
            <div className="aspect-video bg-muted rounded-lg overflow-hidden border border-border">
              <video
                  width="100%"
                  height="100%"
                  src={singleVideoUrl}
                  title={title}
                  className="w-full h-full"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
              />
            </div>
          </div>
        ) : videoTabs ? (
          <div className="bg-background p-4 md:p-6 rounded-xl shadow-lg border border-border">
            <Tabs defaultValue={videoTabs[0].key} className="w-full">
              {videoTabs.map((tab) => (
                <TabsContent
                  key={tab.key}
                  value={tab.key}
                  className="m-0 p-0 mb-4"
                >
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden border border-border relative">
                    {tab.imageUrl ? (
                      <Image
                        src={tab.imageUrl}
                        alt={tab.key}
                        fill
                        className="object-cover"
                        loading="lazy"
                        unoptimized
                      />
                    ) : tab.videoUrl ? (
                      <video
                        width="100%"
                        height="100%"
                        src={tab.videoUrl}
                        title={tab.key}
                        className="w-full h-full"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    ) : null}
                  </div>
                </TabsContent>
              ))}
              <TabsList className="w-full bg-muted/30 h-auto p-2 gap-3 rounded-lg">
                {videoTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    className="flex-1 py-3 px-2 md:px-3 lg:px-6 text-xs md:text-xs lg:text-base font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-[0_2px_8px_rgba(134,239,172,0.4)] data-[state=active]:border data-[state=active]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-background/50 rounded-md transition-all"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        ) : imageTabs ? (
          <div className="bg-background p-4 md:p-6 rounded-xl shadow-lg border border-border">
            <Tabs defaultValue={imageTabs[0].key} className="w-full">
              <TabsContent
                value={imageTabs[0].key}
                className="m-0 p-0 mb-4"
              >
                <div className="bg-muted rounded-lg overflow-hidden border border-border p-2 md:p-4">
                  <div className="relative">
                    <Carousel setApi={setApi} className="w-full">
                      <CarouselContent>
                        {imageTabs[0].imageUrls.map((url, idx) => (
                          <CarouselItem key={idx} className="basis-full">
                            <div className="aspect-video flex items-center justify-center relative">
                              <Image
                                src={url}
                                alt={`${imageTabs[0].key} ${idx + 1}`}
                                fill
                                className="object-contain rounded-lg"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                    <button
                      onClick={() => api?.scrollPrev()}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-background/90 border border-border hover:bg-accent transition-colors shadow-lg z-10"
                      aria-label="Previous image"
                    >
                      <span className="text-foreground text-xl">&lt;</span>
                    </button>
                    <button
                      onClick={() => api?.scrollNext()}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-background/90 border border-border hover:bg-accent transition-colors shadow-lg z-10"
                      aria-label="Next image"
                    >
                      <span className="text-foreground text-xl">&gt;</span>
                    </button>
                  </div>
                </div>
              </TabsContent>
              {imageTabs.slice(1).map((tab) => (
                <TabsContent
                  key={tab.key}
                  value={tab.key}
                  className="m-0 p-0 mb-4"
                >
                  <div className="bg-muted rounded-lg overflow-hidden border border-border p-2 md:p-4">
                    <div className="aspect-video flex items-center justify-center relative">
                      {tab.imageUrls.map((url, idx) => (
                        <Image
                          key={idx}
                          src={url}
                          alt={`${tab.key} ${idx + 1}`}
                          fill
                          className="object-contain rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                </TabsContent>
              ))}
              <TabsList className="w-full bg-muted/30 h-auto p-2 gap-3 rounded-lg">
                {imageTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    className="flex-1 py-3 px-2 md:px-3 lg:px-6 text-xs md:text-xs lg:text-base font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-[0_2px_8px_rgba(134,239,172,0.4)] data-[state=active]:border data-[state=active]:border-border data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-background/50 rounded-md transition-all"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        ) : (
          <div className="aspect-video bg-secondary rounded-xl shadow-lg flex items-center justify-center border border-border">
            <span className="text-muted-foreground font-medium">
              {mediaPlaceholder}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureCard;
